import React, { useState, useEffect, useRef, useCallback } from 'react'
import Select, { components } from 'react-select'
import styled, { useTheme } from 'styled-components'
import debounce from 'lodash-es/debounce'
import { InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { getSelectTheme } from 'Shared/theme'
import PropTypes from 'prop-types'
import { tooltipOverlay } from 'cg/components/shared/tooltipOverlay'

/**
 *
 * @typedef {Object} IconOption
 * @property {string} label Label for the option
 * @property {string} icon icon cssClass for the option
 * @property {string} value Duplicate of icon. icon cssClass for the option
 */

const iconStyle = {
  solid: 'fas',
  regular: 'far',
  light: 'fal',
  duotone: 'fad',
  brands: 'fab'
}

// const initialDefaultIcon = 'fas fa-check'
const initialDefaultIcon = 'far fa-circle-plus'

/** @type IconOption */
const removeOption = {
  label: 'Remove selection',
  icon: 'fas fa-circle-xmark text-danger',
  value: ''
}

const IconPickerSelect = ({ iconName, onSelect, inModal = false, allowRemove = false, position = 'right' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState({ value: iconName !== '' ? iconName : initialDefaultIcon })
  const [iconOptions, setIconOptions] = useState(/** @type Array<IconOption> */ [])
  const [isLoading, setIsLoading] = useState(false)
  const theme = useTheme()

  const inputTextRef = useRef()
  const menuRef = useRef()
  const selectRef = useRef()
  const menuStyle = {
    top: '36px'
  }

  position === 'right' ? (menuStyle.right = '75px') : (menuStyle.left = 0)

  const parseOption = ({ search }) => {
    if (!search || search.length === 0) {
      setIsLoading(false)
      return
    }
    const options = []

    search.forEach(result => {
      const { familyStylesByLicense } = result
      const { pro } = familyStylesByLicense
      const styles = [...new Set(pro.map(item => item.style))]

      for (let i = 0; i < styles.length; i++) {
        if (styles[i] !== 'thin') {
          options.push({
            label: `${result.label} ${styles[i]}`,
            icon: `${iconStyle[styles[i]]} fa-${result.id}`,
            value: `${iconStyle[styles[i]]} fa-${result.id}`
          })
        }
      }
    })

    setIsLoading(false)
    setIconOptions(options)
  }

  /**
   * checkAndPrependRemoveOption
   * @return {Array<IconOption>}
   */
  const checkAndPrependRemoveOption = () => {
    if (value.value === null) {
      return iconOptions
    }
    if (allowRemove && value.value !== initialDefaultIcon) {
      return [removeOption, ...iconOptions]
    }
    return iconOptions
  }

  const fetchIcons = debounce(value => {
    setIsLoading(true)
    fetch('https://api.fontawesome.com', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `query { search(version: "6.5.2", query: "${value}", first: 10) { id, label, familyStylesByLicense { pro { style}} } }`
      })
    })
      .then(res => res.json())
      .then(res => parseOption(res.data))
  }, 300)

  useEffect(() => {
    if (isOpen && iconOptions.length === 0) {
      fetchIcons('check')
    }
  }, [fetchIcons, iconOptions.length, isOpen])

  /** toggleOpen
   *
   * @param {Event} e
   * @returns {void}
   */
  const toggleOpen = e => {
    if (e.target.closest('div[class*=-control]') != null) {
      return
    }
    setIsOpen(!isOpen)
  }

  const onSelectChange = value => {
    if (value.label === removeOption.label) {
      onSelect('')
      setValue({ value: null })
    } else {
      onSelect(value.icon)
      setValue(value)
    }
  }

  return (
    <StyledInputGroupText onClick={toggleOpen} ref={inputTextRef} className="px-2">
      <OverlayTrigger placement="bottom" overlay={overlayProps => tooltipOverlay(<Tooltip>Label Icon</Tooltip>, overlayProps)}>
        <StyledIconWrapper>
          <StyledIcon className={iconName !== '' ? iconName : initialDefaultIcon} />
        </StyledIconWrapper>
      </OverlayTrigger>
      {isOpen && inputTextRef && (
        <IconPopover ref={menuRef} style={menuStyle} $inModal={inModal}>
          <IconMenu>
            <Select
              ref={selectRef}
              onInputChange={fetchIcons}
              autoFocus
              backspaceRemovesValue={false}
              components={{ Option, DropdownIndicator, IndicatorSeparator: null }}
              controlShouldRenderValue={false}
              hideSelectedOptions={false}
              isClearable={false}
              menuIsOpen
              onChange={onSelectChange}
              options={checkAndPrependRemoveOption()}
              placeholder="Search..."
              tabSelectsValue={false}
              value={value}
              styles={SelectStyles({ theme })}
              isLoading={isLoading}
              menuPlacement="auto"
              theme={props => ({
                colors: {
                  ...props.colors,
                  ...getSelectTheme(theme.id)
                },
                borderRadius: 3,
                spacing: {
                  controlHeight: 24,
                  baseUnit: 3,
                  menuGutter: 6
                }
              })}
            />
          </IconMenu>
          <Blanket onClick={() => setIsOpen(false)} />
        </IconPopover>
      )}
    </StyledInputGroupText>
  )
}

IconPickerSelect.propTypes = {
  iconName: PropTypes.string,
  onSelect: PropTypes.func,
  inModal: PropTypes.bool,
  allowRemove: PropTypes.bool,
  position: PropTypes.string
}

const Blanket = props => (
  <div
    style={{
      bottom: 0,
      left: 0,
      top: 0,
      right: 0,
      position: 'fixed',
      zIndex: 100
    }}
    {...props}
  />
)

const Svg = p => <svg width="20" height="20" viewBox="0 0 24 24" focusable="false" role="presentation" {...p} />

const DropdownIndicator = () => (
  <div style={{ height: 24, width: 28 }}>
    <Svg>
      <path
        d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </Svg>
  </div>
)

const Option = props => {
  const { data, isSelected } = props
  const { icon, label } = data

  return (
    <components.Option {...props}>
      <SidebarMenu>
        <IssueTypeIcon>
          <i className={`${icon}`} />
        </IssueTypeIcon>
        <LabelContainer>{label}</LabelContainer>
      </SidebarMenu>
    </components.Option>
  )
}

Option.propTypes = {
  data: PropTypes.shape({
    icon: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string
  }),
  isSelected: PropTypes.bool
}

const SelectStyles = ({ theme }) => ({
  control: provided => ({ ...provided, minWidth: 220, margin: 8, fontSize: '13.75px', transition: 'none' }),
  option: (provided, state) => ({
    ...provided,
    outline: 'none'
  }),
  menu: provided => ({
    ...provided,
    boxShadow: 'none',
    border: 'none',
    position: 'relative',
    fontSize: '13.75px',
    zIndex: 102
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 9999,
    boxSizing: 'content-box',
    padding: '0.5rem',
    translate: '-9px',
    backgroundColor: `${theme.menuItemBg}`,
    borderLeft: `1px solid ${theme.borderColor}`,
    borderRight: `1px solid ${theme.borderColor}`,
    borderBottom: `1px solid ${theme.borderColor}`,
    borderTop: 'none',
    borderRadius: '0 0 0.25rem 0.25rem'
  })
})

const StyledInputGroupText = styled(InputGroup.Text)`
  &:hover,
  &:focus {
    background-color: ${props => props.theme.hoverBgLight};
  }
`

const SidebarMenu = styled.span`
  display: flex;
  align-content: center;
`

const IssueTypeIcon = styled.span`
  display: block;
  text-align: center;
  font-size: 14.5px;
  border-radius: 3px;
  height: 20px;
  width: 22px;
  min-width: 22px;
  margin-right: 12px;
`

const IconPopover = styled.div`
  position: absolute;
  top: 24px;
  padding-top: 5px;
  padding-bottom: 5px;
  z-index: ${props => (props.$inModal ? 10003 : 3)};
`

const IconMenu = styled.div`
  position: relative;
  z-index: 101;
  border: 1px solid ${props => props.theme.borderColor};
  overflow: hidden;
  background: ${props => props.theme.modalBg};
  border-radius: 4px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
`

export const StyledIconWrapper = styled.div`
  display: ${props => (props.$inline ? 'inline-block' : 'block')};
  min-width: 20px;
`

export const StyledIcon = styled.i`
  color: ${props => (props.$color ? props.$color : props.theme.textGreyTinted)};
`

const LabelContainer = styled.span`
  text-transform: capitalize;
`

export default IconPickerSelect
